'use client';

/**
 * ChargeViaAPI Component
 *
 * Shared headless processor for API-driven payments.
 * Used by Credit Card (via Checkout.com) and as the universal processor for all quick top-ups.
 *
 * Does NOT render any UI — parent uses ChargeStatusDisplay for display.
 *
 * Flow:
 * 1. Get processing configuration (required_params, optional 3DS config)
 * 2. Handle 3D Secure authentication if required (CC only — PayPal won't return three_ds_config)
 * 3. Create purchase (POST /my/user_credits) with device_session_id from Risk.js
 * 4. Poll for completion status
 */

import React, { useEffect } from 'react';
import { usePurchaseLog } from '../../config/PurchaseFlowConfigProvider';
import { usePurchaseAnalytics } from '../../analytics';
import { getClientPlatformForAPI, browserContext } from '../../utils/platform';
import {
  getProcessingData,
  createPurchase,
  getPurchaseStatus,
  ApiError
} from '../../api';
import { create3DSIframe } from '../../utils/create3DSIframe';
import { poll } from '../../utils/polling';
import type { ChargeProcessorProps } from '../../types';

export const ChargeViaAPI: React.FC<ChargeProcessorProps> = ({
  amount,
  withBonus = false,
  purchaseType,
  advisorId,
  clickSource,
  purchaseContext,
  viaSite,
  extraParams,
  fxRate,
  currency,
  riskModel,
  riskModelConfig,
  onStatusChange,
  onSuccess,
  onError,
  startSignal = 0
}) => {
  const log = usePurchaseLog();
  const analytics = usePurchaseAnalytics();

  useEffect(() => {
    if (!startSignal) return;

    const processPayment = async () => {
      try {
        // Step 1: Get processing configuration
        onStatusChange?.('processing', 'Loading payment configuration...');

        const parsedExtraParams = extraParams
          ? Object.fromEntries(new URLSearchParams(extraParams).entries())
          : {};

        const processingData = await getProcessingData(amount, {
          use_reference_transaction: true,
          with3ds: true,
          with_bonus: withBonus,
          purchase_type: purchaseType?.toLowerCase(),
          context: purchaseContext,
          platform: getClientPlatformForAPI(),
          ...browserContext,
          ...(clickSource && { click_source: clickSource }),
          ...(advisorId && { intended_advisor_id: advisorId }),
          ...(viaSite && { via_site: true }),
          ...(currency && { currency }),
          ...(fxRate && { fx_rate: fxRate }),
          ...parsedExtraParams
        });

        analytics.setConversionEventId(processingData.conversion_event_id);
        analytics.trackPurchaseStarted();

        // Step 2: Handle 3D Secure authentication if required
        // (only triggered for credit cards — PayPal backend won't return three_ds_config)
        let threeDsData: any = null;
        if (processingData.three_ds_config) {
          onStatusChange?.('authenticating', '3D Secure authentication required...');

          threeDsData = await new Promise<any>((resolve, reject) => {
            const cleanup = create3DSIframe(
              {
                acsUrl: processingData.three_ds_config.acs_url,
                url: processingData.three_ds_config.url
              },
              {
                onComplete: (data) => {
                  cleanup();
                  resolve(data);
                },
                onError: (error) => {
                  cleanup();
                  reject(error);
                }
              }
            );
          });

          onStatusChange?.('processing', 'Completing payment...');
        }

        // Step 3: Create purchase
        onStatusChange?.('processing', 'Processing payment...');
        const purchaseResult = await createPurchase({
          requiredParams: {
            ...processingData.required_params,
            ...(threeDsData || {})
          },
          riskModel,
          riskModelConfig,
          fxRate,
          currency
        });

        if (purchaseResult.error) {
          log('error', 'Payment Processing', 'Create purchase error', {
            errorMessage: purchaseResult.error,
            errorType: purchaseResult.errorType
          });
          onError({
            success: false,
            errorMessage: purchaseResult.error,
            errorType: purchaseResult.errorType,
            errorTitle: purchaseResult.errorTitle,
            recoverable: purchaseResult.recoverable,
            retriable: purchaseResult.retriable,
            errorOrigin: purchaseResult.errorOrigin,
            errorPathname: purchaseResult.errorPathname,
            spendingLimits: purchaseResult.spendingLimits
          });
          return;
        }

        if (!purchaseResult.id) {
          onError({ success: false });
          return;
        }

        // Step 4: Poll for completion
        onStatusChange?.('polling', 'Processing your payment...');

        const response = await poll({
          pollFunction: () => getPurchaseStatus(
            purchaseResult.id,
            getClientPlatformForAPI()
          ),
          checkComplete: (res) => !!(res.okMessage || res.errorMessage)
        });

        analytics.setExtraEventParams(response.extraEventParams);

        if (response.errorMessage) {
          log('error', 'Payment Processing', 'API charge processing error (poll)', {
            errorMessage: response.errorMessage,
            errorType: response.errorType
          });
          onError({
            success: false,
            errorMessage: response.errorMessage,
            errorType: response.errorType,
            errorTitle: response.errorTitle,
            recoverable: response.recoverable,
            retriable: response.retriable,
            errorOrigin: response.errorOrigin,
            errorPathname: response.errorPathname,
            spendingLimits: response.spendingLimits
          });
          return;
        }

        // Success!
        onSuccess({
          success: true,
          userCreditId: purchaseResult.id,
          okMessage: response.okMessage,
          cashbackAmount: response.cashbackAmount,
          pathname: response.pathname,
          threeDAuthResult: response.threeDAuthResult
        });
      } catch (error) {
        log('error', 'Payment Processing', 'API charge processing error', { error });

        onError({ success: false, errorMessage: error instanceof ApiError ? error.userMessage : undefined });
      }
    };

    processPayment();
  }, [startSignal, log]);

  // Headless component — no UI rendered
  // 3DS iframe is created via create3DSIframe utility (outside React tree)
  return null;
};
